<template>
    <Modal id="cart-fields-modal"
           icon="fa-memo-pad"
           size="lg"
           :shaded-body="true"
           :cancel="cancel"
           :ok="submit"
           :ok-enabled="fieldGroups.length > 0"
           title="CART_FIELDS.MODAL_TITLE">
        <NoResults v-if="fieldGroups.length < 1"
                   icon="fa-solid fa-align-slash"
                   lang="CART_FIELDS.NO_FIELDS" />
        <form v-else
              id="custom-fields-form"
              novalidate
              class="form-v form-v--extra-spacing form--shade"
              @submit.prevent="submit">
            <ErrorMessage v-if="initialError"
                          :error="initialError" />

            <FieldGroup v-for="(fieldGroup) in fieldGroups"
                        :key="fieldGroup.itemInstanceID"
                        :field-group="fieldGroup" />

            <!-- Having a submit button makes it so you can press enter to submit the form. -->
            <input type="submit"
                   class="hidden" />
        </form>
    </Modal>
</template>

<script>
import ErrorMessage from '@/vue/components/Errors/ErrorMessage';
import FieldGroup from '@/vue/components/CustomFields/FieldGroup';
import FieldInstanceFilter from '@/lib/CustomFields/FieldInstanceFilter';
import Modal from '@/vue/components/Modals/Modal';
import NoResults from '@/vue/components/NoResults';
import { cloneDeep } from '@/functions/clone';
import { container } from '@/container';
import { extractFieldValuesFromForm } from '@/lib/CustomFields/fieldForm';
import { filterFieldGroups } from '@/lib/CustomFields';
import {
    getAllFieldGroupsFromOrder,
    getAllFieldInstancesFromGroups,
    setValuesOfFieldGroup,
} from '@/lib/CustomFields/fieldFunctions';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';

export default {
    components: { ErrorMessage, NoResults, FieldGroup, Modal },

    mixins: [
        ModalPropsMixin,
    ],

    props: {
        /**
         * @type {Cart}
         */
        cart: {
            type: Object,
            required: true,
        },

        /**
         * @type {UserFacingError|null}
         */
        initialError: {
            type: Object,
            default: null,
        },
    },

    data() {
        return {
            hasError: false,

            /**
             * @type {FieldGroup[]}
             */
            fieldGroups: [],
        };
    },

    computed: {},

    created() {
        /**
         * Grab all the field groups in the cart and clone them.
         * This way we don't modify the originals while editing the form. That shouldn't happen until OK is clicked, at
         * which point the values are sent back to the calling code and that will set the values on the field instances.
         */
        this.fieldGroups = cloneDeep(getAllFieldGroupsFromOrder(this.cart));

        // Remember the last fields that were shown to the user.
        this.cart.lastPresentedCustomFieldKeys = getAllFieldInstancesFromGroups(this.fieldGroups)
            .map((fieldInstance) => fieldInstance.getKey());

        // Filter the instances in each group to only show non-ask-early fields.
        this.fieldGroups = filterFieldGroups(this.fieldGroups, { askEarly: false });

        if (this.initialError) {
            this.$nextTick(() => {
                this.validate();
            });
        }
    },

    methods: {
        validate() {
            // Grab the field data from the form.
            const fieldValues = extractFieldValuesFromForm();

            // Set those values on the field groups so we can run validation on the objects.
            // Here we only set the "non ask early" field values because these are the only ones displayed by the modal.
            this.fieldGroups.forEach(
                (fieldGroup) => setValuesOfFieldGroup(
                    fieldValues,
                    fieldGroup,
                    FieldInstanceFilter.NON_ASK_EARLY_FIELDS_ONLY,
                    true,
                )
            );

            // Validate the fields.
            this.hasError = false;
            this.fieldGroups.forEach((fieldGroup) => {
                if (!container.fieldValidator.validateFieldGroup(fieldGroup)) {
                    this.hasError = true;
                }
            });

            if (this.hasError) {
                // Scroll the modal to the first input with the error.
                this.$nextTick(() => {
                    const firstErrorGroup = document.querySelector('#cart-fields-modal .form-group.has-error');
                    const errorInput = firstErrorGroup.querySelector('input, textarea, select');
                    if (errorInput) {
                        // If there is an input to focus on do that.
                        errorInput.focus();
                    } else {
                        // Otherwise just scroll to the group with the error.
                        firstErrorGroup.scrollIntoView();
                    }
                });
                return false;
            }

            return true;
        },

        submit() {
            const valid = this.validate();
            if (!valid) {
                return;
            }

            // Grab the field data from the form.
            const fieldValues = extractFieldValuesFromForm();

            this.resolve({ fieldValues });
        },
    },
};
</script>
